import type {
  ProductCardGridProps,
  ProductCardListItemProps,
  ProductCardProductData,
  ProductCardBadgeData,
  ProductCardOptionsData,
  ProductCardImage,
} from '@dutchiesdk/ecommerce-extensions-sdk';

import type {
  ProductCardProduct,
  ProductCardImage as InternalImage,
  ProductCardBadges,
  ProductCardPricing,
  ProductCardOptions,
} from 'src/components/product-card-v2/types';
import { cleanImgixSrc } from 'shared/components/imgix';

/** Maps internal GqlProducts_Images to SDK-stable image shape, normalizing URLs through Imgix */
const mapImages = (image: InternalImage): ProductCardImage[] =>
  image.images
    .filter((img) => img.active && img.url)
    .map((img) => ({ url: cleanImgixSrc(img.url), description: img.description ?? undefined }));

/** Maps internal product identity to SDK shape */
const mapProduct = (product: ProductCardProduct, image: InternalImage): ProductCardProductData => ({
  id: product.id,
  name: product.name,
  brandName: product.brandName,
  href: product.href,
  strainType: product.strainType,
  potency: product.potency,
  avgRating: product.avgRating,
  reviewCount: product.reviewCount,
  images: mapImages(image),
  fallbackImageUrl: cleanImgixSrc(image.fallbackImage),
});

/** Maps internal badge data to SDK shape, extending with isComingSoon for internal use */
const mapBadges = (badges: ProductCardBadges): ProductCardBadgeData & { isComingSoon: boolean } => ({
  isStaffPick: badges.isStaffPick,
  isSponsored: badges.isSponsored,
  isSpecialOffer: badges.isSpecialOffer,
  isComingSoon: badges.isComingSoon,
  specialNames: badges.specialNames,
  hideSalesLanguage: badges.hideSalesLanguage,
  collectionBadge: badges.collectionBadge,
});

/** Maps internal per-option data to SDK shape, omitting internal-only fields */
const mapOptions = (options: ProductCardOptions): ProductCardOptionsData => ({
  items: options.items.map((item) => ({
    value: item.value,
    label: item.label,
    currentPrice: item.currentPrice,
    originalPrice: item.originalPrice,
    discountLabel: item.discountLabel,
  })),
  totalCount: options.totalCount,
  hasMultiple: options.hasMultiple,
  shouldDisplayLabel: options.shouldDisplayLabel,
});

type GridDataInput = {
  product: ProductCardProduct;
  image: InternalImage;
  badges: ProductCardBadges;
  pricing: ProductCardPricing;
  options: ProductCardOptions;
  productIndex: number;
};

type GridCallbacks = {
  onLinkClick: () => void;
  onAddToCart: () => void;
};

/** Maps internal card data to SDK ProductCardGridProps for remote grid cards */
export const mapToGridProps = (data: GridDataInput, callbacks: GridCallbacks): ProductCardGridProps => ({
  product: mapProduct(data.product, data.image),
  pricing: {
    displayPrice: data.pricing.displayPrice,
    standardPrice: data.pricing.standardPrice,
    discount: data.pricing.discount,
    weight: data.pricing.weight,
    showWeight: data.pricing.showWeight,
    buttonCopy: data.pricing.buttonCopy,
  },
  options: mapOptions(data.options),
  badges: mapBadges(data.badges),
  onLinkClick: callbacks.onLinkClick,
  onAddToCart: callbacks.onAddToCart,
  productIndex: data.productIndex,
});

type ListItemDataInput = {
  product: ProductCardProduct;
  image: InternalImage;
  badges: ProductCardBadges;
  pricing: ProductCardPricing;
  options: ProductCardOptions;
  productIndex: number;
};

type ListItemCallbacks = {
  onLinkClick: () => void;
  onOptionClick: (value: string) => void;
};

/** Maps internal card data to SDK ProductCardListItemProps for remote list cards */
export const mapToListItemProps = (
  data: ListItemDataInput,
  callbacks: ListItemCallbacks
): ProductCardListItemProps => ({
  product: mapProduct(data.product, data.image),
  pricing: {
    displayPrice: data.pricing.displayPrice,
    standardPrice: data.pricing.standardPrice,
    discount: data.pricing.discount,
    weight: data.pricing.weight,
    showWeight: data.pricing.showWeight,
    buttonCopy: data.pricing.buttonCopy,
  },
  options: mapOptions(data.options),
  badges: mapBadges(data.badges),
  onLinkClick: callbacks.onLinkClick,
  onOptionClick: callbacks.onOptionClick,
  productIndex: data.productIndex,
});
